import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { RecordUpdateLifecycle } from "~/common/types/graphql";
import {
  ProceedRecordUpdateDataImportProjectDocument,
  type ProceedRecordUpdateDataImportProjectMutation,
  type ProceedRecordUpdateDataImportProjectMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/** The outcome of a proceed call: the record-update DIP/data source plus the jobs to submit. */
type RecordUpdateProceedResult =
  ProceedRecordUpdateDataImportProjectMutation["cnutProceedRecordUpdateDataImportProject"];

type ProceedRecordUpdateOptions = {
  evidenceIds?: string[];
  dataImportProjectId?: string;
};

// The backend refusals all arrive as DB_ERROR.REQUEST_ERROR, so translateError
// would collapse them into one generic message. Match on the (API-stable)
// message text instead to keep each refusal actionable for the operator.
// Literal t() calls only — i18next-cli prunes keys it cannot see statically.
const translateRefusal = (t: ReturnType<typeof useTranslation>["t"], message: string): string | null => {
  if (message.includes("missing their evidence-lineage identity")) {
    return t("feature.recordUpdate.proceed.error.identityMissing");
  }
  if (message.includes("conflicting identity")) {
    return t("feature.recordUpdate.proceed.error.conflict");
  }
  if (message.includes("preview changed since it was reviewed")) {
    return t("feature.recordUpdate.proceed.error.staleToken");
  }
  return null;
};

/**
 * Applies the record-update workflow after operator confirmation: creates the
 * record-update DIP. The backend REFUSES when any record has a conflicting or
 * missing evidence-lineage identity, or when the confirmationToken (from the
 * preview for the same recordIds/tenant) has gone stale — refusals surface as
 * actionable toasts.
 */
export const useProceedRecordUpdate = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [proceedMutation, { loading }] = useMutation<
    ProceedRecordUpdateDataImportProjectMutation,
    ProceedRecordUpdateDataImportProjectMutationVariables
  >(ProceedRecordUpdateDataImportProjectDocument);

  const proceed = useCallback(
    async (
      recordIds: string[],
      confirmationToken: string,
      lifecycle: RecordUpdateLifecycle = "edit",
      { evidenceIds = [], dataImportProjectId }: ProceedRecordUpdateOptions = {},
    ): Promise<RecordUpdateProceedResult | null> => {
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const { data } = await proceedMutation({
          variables: { input: { tenantId, recordIds, evidenceIds, confirmationToken, lifecycle, dataImportProjectId } },
        });
        return data?.cnutProceedRecordUpdateDataImportProject ?? null;
      } catch (e) {
        const refusal = e instanceof Error ? translateRefusal(t, e.message) : null;
        toast.error(refusal ?? translateError(t, e));
        return null;
      }
    },
    [proceedMutation, tenantId, t],
  );

  return { proceed, proceeding: loading };
};
